import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiError } from "@/lib/api";
import { GraduationCap, CircleNotch, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("student");
  const [form, setForm] = useState({ name: "", email: "", password: "", rut: "", company_name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register({ ...form, role });
      toast.success("¡Cuenta creada!");
      navigate("/dashboard");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-blue-50 via-white to-blue-100 text-foreground p-12 border-r border-border">
        <Link to="/" className="flex items-center">
          <Logo className="h-9" />
        </Link>
        <div>
          <h2 className="font-display text-4xl font-800 tracking-tight leading-none text-foreground">
            Comienza tu ruta<br />de capacitación hoy.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-md leading-relaxed">
            Regístrate como alumno o crea una cuenta empresa para capacitar a tu equipo.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">© 2026 NovaCert OTEC</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <p className="text-xs uppercase tracking-overline text-primary mb-2">Registro</p>
          <h1 className="font-display text-3xl font-800 tracking-tight mb-5">Crea tu cuenta</h1>

          <Tabs value={role} onValueChange={setRole} className="mb-5">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="student" data-testid="register-role-student">Alumno</TabsTrigger>
              <TabsTrigger value="company" data-testid="register-role-company">Empresa</TabsTrigger>
              <TabsTrigger value="instructor" data-testid="register-role-instructor">Relator</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">{role === "company" ? "Nombre de contacto" : "Nombre completo"}</Label>
              <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name" className="mt-1.5" />
            </div>
            {role === "company" && (
              <div>
                <Label htmlFor="company">Razón social</Label>
                <Input id="company" value={form.company_name} onChange={set("company_name")} required
                  data-testid="register-company" className="mt-1.5" placeholder="Empresa SpA" />
              </div>
            )}
            <div>
              <Label htmlFor="rut">RUT {role === "company" && "(empresa)"}</Label>
              <Input id="rut" value={form.rut} onChange={set("rut")} data-testid="register-rut" className="mt-1.5" placeholder="12.345.678-9" />
            </div>
            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required
                minLength={6} data-testid="register-password" className="mt-1.5" placeholder="Mínimo 6 caracteres" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="register-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full bg-primary hover:bg-blue-600 text-white">
              {loading ? <CircleNotch className="animate-spin" /> : "Crear cuenta"}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">o</span><div className="h-px bg-border flex-1" />
          </div>
          <Button type="button" variant="outline" onClick={googleLogin} data-testid="register-google"
            className="w-full gap-2">
            <GoogleLogo size={18} weight="bold" /> Registrarse con Google
          </Button>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            ¿Ya tienes cuenta? <Link to="/login" className="text-primary font-medium hover:underline">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
